import React from "react";

/**
 * Global error boundary. Catches uncaught render/effect errors anywhere below
 * it in the tree and shows a friendly fallback instead of leaving the user
 * staring at a white screen. Also captures the stack + component location
 * into `state.error` so we can offer a "copy error" button for support.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        this.setState({ info });
        // Best-effort console log so the pod log still has the trace.
        // eslint-disable-next-line no-console
        console.error("ErrorBoundary caught:", error, info?.componentStack);
    }

    handleReload = () => {
        try {
            this.setState({ error: null, info: null });
        } catch { /* noop */ }
        window.location.reload();
    };

    handleHome = () => {
        window.location.href = "/";
    };

    handleCopy = async () => {
        const { error, info } = this.state;
        const blob = [
            `URL: ${window.location.href}`,
            `Time: ${new Date().toISOString()}`,
            `Error: ${error?.name || "Error"}: ${error?.message || ""}`,
            `Stack:\n${error?.stack || ""}`,
            `Component stack:${info?.componentStack || ""}`,
        ].join("\n\n");
        try {
            await navigator.clipboard.writeText(blob);
        } catch { /* noop */ }
    };

    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "var(--asl-navy-50,#F5F6F8)" }} data-testid="error-boundary">
                <div className="asl-card max-w-lg w-full p-8 text-center">
                    <div className="text-5xl asl-crimson-text mb-3">⚠</div>
                    <h1 className="text-lg font-bold asl-navy-text mb-2">Something went wrong.</h1>
                    <p className="text-sm text-[color:var(--asl-muted)] mb-4">
                        This screen ran into an unexpected error. Try refreshing the page, or go back to Home and re-navigate.
                    </p>
                    <details className="text-left text-[11px] mono text-[color:var(--asl-muted)] mb-4 bg-slate-50 rounded p-2">
                        <summary className="cursor-pointer text-[color:var(--asl-muted)]">Error details</summary>
                        <div className="mt-2 whitespace-pre-wrap break-words" data-testid="error-boundary-message">
                            {String(this.state.error?.message || this.state.error)}
                        </div>
                    </details>
                    <div className="flex items-center justify-center gap-2">
                        <button
                            type="button"
                            onClick={this.handleReload}
                            className="px-4 py-2 rounded-md bg-[color:var(--asl-navy)] text-white text-sm hover:opacity-90"
                            data-testid="error-boundary-reload"
                        >
                            Refresh
                        </button>
                        <button
                            type="button"
                            onClick={this.handleHome}
                            className="px-4 py-2 rounded-md border border-[color:var(--asl-border,#E5E7EB)] text-sm hover:bg-gray-50"
                            data-testid="error-boundary-home"
                        >
                            Go Home
                        </button>
                        <button
                            type="button"
                            onClick={this.handleCopy}
                            className="px-4 py-2 rounded-md border border-[color:var(--asl-border,#E5E7EB)] text-sm hover:bg-gray-50"
                            data-testid="error-boundary-copy"
                        >
                            Copy error
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
